import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonLoading,
  IonRow,
} from '@ionic/react'
import { addCircleOutline, trash, trashOutline } from 'ionicons/icons'
import { ICrewMember } from '../store/crew.types'
import { getCrewListSelector, getCrewListDataSelector } from '../selectors'
import { onAddCrewMember, setCrewList, setName } from '../store/crew.actions'
import './CrewList.component.css'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import CrewRoleSelect from './CrewRoleSelect.component'
import { IOption } from '../../../common/common.types'
import { IPHIComponent } from '../../../components/phi/phi.component'
import { getClientDataImportErrorSelector } from '../../clientData/selectors/getClientDataImportError.selector'
import { showAlert } from '../../vitals/store/vitals.actions'
import { CrewConstants } from '../store/crew.constants'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'

export const CrewList: React.FC<IPHIComponent> = (props: IPHIComponent) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { isPHI } = props

  const crewList = useSelector(getCrewListSelector)
  const crewListData = useSelector(getCrewListDataSelector)
  const clientDataImportError = useSelector(getClientDataImportErrorSelector)
  const serviceConfig = useSelector(getServiceConfigSelector)
  const { PR_CREW_ROLE } = serviceConfig
  const closeSliding = () => {
    //@ts-ignore
    document.querySelector('ion-item-sliding') && document.querySelector('ion-item-sliding').closeOpened()
  }

  useEffect(() => {
    window.addEventListener('mousedown', closeSliding)
    return () => {
      window.removeEventListener('mousedown', closeSliding)
    }
  }, [])

  const hasClientDataError = !!clientDataImportError

  if (!hasClientDataError && !crewListData?.length) {
    return <IonLoading isOpen animated showBackdrop spinner={'crescent'} />
  }

  const nameOnChange = (option: IOption, crewMember: ICrewMember) => {
    if (!option || String(crewMember.userId?.id) === String(option.id)) return
    dispatch(setName(option, crewMember))
  }

  const addCrewMember = () => {
    if (crewList && crewList.length <= CrewConstants.ADD_CREW_MEMBER_LIMIT) {
      dispatch(onAddCrewMember())
    } else {
      dispatch(showAlert({ message: 'Maximum limit reached' }))
    }
  }

  const deleteCrewMember = (crewMember: ICrewMember) => {
    if (crewList && crewList.length > CrewConstants.MIN_NO_OF_CREW_MEMBERS_DEL) {
      const newCrewList = crewList.filter((crew) => crew.key !== crewMember.key)
      dispatch(setCrewList(newCrewList))
    } else {
      dispatch(
        showAlert({
          message: `Delete failed! Minimum ${CrewConstants.MIN_NO_OF_CREW_MEMBERS_DEL} crew members are required`,
        })
      )
    }
  }

  const isPrimaryCrewMember = (crewMember: ICrewMember, index: number) => {
    return !!crewMember && index === 0
  }

  return (
    <IonList>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' slot='end' color='medium'>
          {t('shift.SwipeToDelete')}
        </IonLabel>
      </IonItemDivider>
      {crewList.map((crewMember: ICrewMember, index: number) => {
        const isPrimaryCrew = isPrimaryCrewMember(crewMember, index)
        return (
          <IonItemSliding disabled={isPrimaryCrew} key={crewMember.key}>
            <IonCard className='ion-no-margin ion-margin-vertical' data-testid='CrewMember'>
              <IonCardContent>
                <IonGrid className='ion-no-padding'>
                  <IonRow>
                    <IonCol>
                      {t('shift.crewDetails.form.CrewMember')} {index + 1}
                    </IonCol>
                    <IonButtons>
                      <IonButton
                        className='crew-delete-icon'
                        data-testid='delete'
                        hidden={isPrimaryCrew}
                        onClick={() => deleteCrewMember(crewMember)}>
                        <IonIcon icon={trashOutline} />
                      </IonButton>
                    </IonButtons>
                  </IonRow>

                  <IonRow>
                    <IonCol className='picklist'>
                      <SingleSelect
                        isPHI={isPHI}
                        dataTestID={`CrewMember${crewMember.userId}`}
                        selectedOption={crewMember.userId}
                        label={t('shift.crewDetails.form.CrewMember')}
                        listHeader={t('shift.crewDetails.form.CrewMember')}
                        options={crewListData}
                        action={nameOnChange}
                        dispatchData={crewMember}
                        hideClearButton={true}
                        disabled={isPrimaryCrew}
                      />
                    </IonCol>
                  </IonRow>

                  {PR_CREW_ROLE === 1 && (
                    <>
                      <hr className='field-divider' />
                      <IonRow>
                        <IonCol className='picklist'>
                          <CrewRoleSelect crewMember={crewMember} isPHI={isPHI} />
                        </IonCol>
                      </IonRow>
                      <hr className='field-divider' />
                    </>
                  )}
                  <IonItem className='ion-hide' />
                </IonGrid>
              </IonCardContent>
            </IonCard>
            <IonItemOptions side='end' className='delete-swipe' data-testid='Swipe'>
              <IonItemOption
                color='danger'
                onDrag={() => deleteCrewMember(crewMember)}
                onClick={() => deleteCrewMember(crewMember)}>
                <IonIcon slot='icon-only' icon={trash} />
              </IonItemOption>
            </IonItemOptions>
          </IonItemSliding>
        )
      })}
      <IonRow className='ion-float-right'>
        <IonCol>
          <IonItem lines='none'>
            <IonButton
              className='transform-none multi-card-add-button'
              fill='clear'
              color='primary-alt'
              onClick={addCrewMember}>
              <IonIcon icon={addCircleOutline} />
              <IonLabel>{t('shift.crewDetails.buttons.AddCrewMember')}</IonLabel>
            </IonButton>
          </IonItem>
        </IonCol>
      </IonRow>
    </IonList>
  )
}
